import { createApp } from "vue";
import DOMUtils from "@utils/dom.utils";
import RandomUtils from "@utils/random.utils";
import { isDefined, isEmpty, notNull } from "@utils/var.check";
import { MEDIA_TYPE } from "@plugins/media/media.constant";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import GoogleAttachment from "@plugins/google/components/GoogleAttachment.vue";
import TextToSpeech from "@plugins/richtext/plugins/text-to-speech/components/TextToSpeech.vue";
import DynamicLinkUtils from "@plugins/dynamic-link/dynamic.link.util";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import ScrollUtils from "@utils/scroll.utils";
import { nextTick } from "vue";

let lastBlankId = 0;
export const generateBlankId = (): number => {
  const now = Date.now();
  lastBlankId = now > lastBlankId ? now : lastBlankId + 1;
  return lastBlankId;
};

export default {
  // TODO: to split the file to multiple smaller files (media.utils.js)

  convertLegacyMediaObjects(container) {
    const selector =
      ".media-object.html5[data-uuid]" +
      ",.media-object.mo-download[data-uuid]" + // get doc type MO
      ",.media-object.video[data-uuid]" + // get video type MO
      ",.media-object.audio[data-uuid]" + // get audio type MO
      ",.media-object.image[data-uuid]" + // get image type MO
      ",.image[data-src*='mediaobject'][data-uuid]" + // get legacy image type MO
      ",img[data-type=image][data-uuid]"; // get other legacy image type MO
    DOMUtils.elOf(container, selector).forEach((element) => {
      const data = populateLegacyFormatData(element);
      const mediaObject = data.mediaobject;
      const uuid = mediaObject.uuid;
      const mediaObjectTag = this.createInlineMediaObjectTag(mediaObject);

      const selectors =
        ".media-object.html5[data-uuid='" + // get html5 type MO
        uuid +
        "']" +
        ",.media-object.mo-download[data-uuid='" +
        uuid +
        "']" +
        ",.media-object.video[data-uuid='" + // get doc type MO
        uuid +
        "']" +
        ",.media-object.audio[data-uuid='" + // get video type MO
        uuid +
        "']" +
        ",.media-object.image[data-uuid='" + // get audio type MO
        uuid +
        "']" +
        ",.image[data-src*='mediaobject'][data-uuid='" +
        uuid +
        "']" +
        ",img[data-type=image][data-uuid='" +
        uuid +
        "']";
      const targetElement = DOMUtils.elOf(container, selectors)[0];
      targetElement.replaceWith(mediaObjectTag);
    });

    function populateLegacyFormatData(element) {
      const uuid = element.getAttribute("data-uuid");
      let metadata = element.getAttribute("data-metadata");
      let type: string;
      if (element.classList.contains("video")) {
        type = "video";
      } else if (element.classList.contains("audio")) {
        type = "audio";
      } else if (element.classList.contains("image")) {
        type = "image";
      } else if (element.classList.contains("html5")) {
        type = "html5";
      } else {
        // doc type tag store as data instead of class
        type = element.getAttribute("data-type");
      }

      const dataDisplayStart = element.getAttribute("data-display-start");

      if (type.toLowerCase() === "video" || type.toLowerCase() === "audio") {
        // build metadata for audio and video
        metadata = {
          start: element.getAttribute("data-video-start"),
          end: element.getAttribute("data-video-end"),
        };
      }

      const component = {
        uuid: uuid,
        title: element.getAttribute("data-moTitle"),
        height: undefined,
        width: undefined,
        metadata: metadata,
        dataDisplayStart: dataDisplayStart,
        type: type,
        showEdit: true,
        cssClass: "",
        downloadable: element.getAttribute("data-downloadable") === true,
      };
      return {
        mediaobject: component,
        container: element.closest("div.paragraph"),
      };
    }
  },

  convertLegacyImageMediaObjects(container) {
    const selector = "img[src^='/mediaobject/preview/']";
    const uuidRegex = /[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89aAbB][a-f0-9]{3}-[a-f0-9]{12}/;

    DOMUtils.elOf(container, selector).forEach((element) => {
      const src = element.getAttribute("src");
      if (isEmpty(src)) return;

      const matchedUuids = src?.toLowerCase().match(uuidRegex);
      if (isEmpty(matchedUuids)) return;

      if (matchedUuids) {
        const mediaObjectUuid = matchedUuids[0];

        const mediaObjectTag = this.createInlineMediaObjectTag({
          uuid: mediaObjectUuid,
          type: "image",
        });

        const targetSelector = "img[src^='/mediaobject/preview/" + mediaObjectUuid + "']";

        const targetElement = DOMUtils.elOf(container, targetSelector)[0];
        targetElement.replaceWith(mediaObjectTag);
      }
    });
  },

  convertLegacyExternalResource(container) {
    DOMUtils.elOf(container, ".external-resource img").forEach((image) => {
      const media = this.convertExternalImage(image);
      const resourceWrapper = image.closest(".external-resource");
      resourceWrapper?.replaceWith(media);
    });
  },

  insertInlineSpeechEvaluation(editor, data) {
    const placeholder = editor.dom.createHTML("div", {
      class: "sls-speech-evaluation",
      "data-speech-evaluation-text": data,
    });
    editor.insertContent(placeholder);
  },

  convertExternalMedia(container) {
    const images = DOMUtils.elOf(container, "img");
    images.forEach((image) => {
      const parent = image.parentNode;
      const mediaSrc = DOMUtils.attr(image, "src");
      const isInvalidMedia = isEmpty(mediaSrc);
      const isBlobMediaUrl = mediaSrc.startsWith("blob:");
      const isDataMediaUrl = mediaSrc.startsWith("data:");

      if (isInvalidMedia || isBlobMediaUrl || isDataMediaUrl) {
        parent.removeChild(image);
      } else {
        const media = this.convertExternalImage(image);
        parent.replaceChild(media, image);
      }
    });
  },

  convertExternalImage(image) {
    const attrs = {
      class: "sls-external-media",
      "data-uuid": RandomUtils.randomUuid(),
      "media-type": "IMAGE",
      "data-type": "external",
      "data-title": isEmpty(DOMUtils.attr(image, "alt")) ? "" : DOMUtils.attr(image, "alt"),
      "data-url": DOMUtils.attr(image, "src"),
    };

    const mediaWidth = DOMUtils.attr(image, "width");
    const mediaHeight = DOMUtils.attr(image, "height");

    if (mediaWidth && mediaHeight) {
      attrs["data-width"] = mediaWidth;
      attrs["data-height"] = mediaHeight;
    }

    return DOMUtils.createElement("div", attrs);
  },

  /**
   * Function to create media data tag. Supports MO and UGC.
   * @param mediaData - Data to construct the tag.
   * @returns {*}
   */
  createInlineMediaTag(mediaData) {
    if (mediaData.type === "mo") {
      return this.createInlineMediaObjectTag({
        uuid: mediaData.uuid,
        type: mediaData.mediatype,
        start: mediaData.cuePoints.start,
        end: mediaData.cuePoints.end,
        height: mediaData.height,
        width: mediaData.width,
        dataDisplayStart: mediaData.dataDisplayStart,
        resourceDomain: mediaData.resourceDomain,
        strictMode: mediaData.strictMode,
        isTranscriptEnabled: mediaData.isTranscriptEnabled,
        transcriptionFileVersion: mediaData.transcriptionFileVersion,
      });
    } else if (mediaData.type === "ugc") {
      return this.createInlineUgcMediaTag({
        uuid: mediaData.uuid,
        type: mediaData.mediatype,
      });
    }
    return null;
  },

  /**
   * Create media object html object
   */
  createInlineMediaObjectTag(data) {
    const attr = {
      class: "sls-media",
      name: "sls-media",
      "data-type": "mo",
      "data-uuid": data.uuid,
      "media-type": data.type.toUpperCase(),
      "data-transcript-enabled": data.isTranscriptEnabled,
      "transcription-file-version": data.transcriptionFileVersion,
    };
    if (data.type.toLowerCase() === "image" && data.height && data.width) {
      const height = data.height;
      const width = data.width;
      attr["data-height"] = height;
      attr["data-width"] = width;
    }
    let cueStart, cueEnd;
    if (data.metadata) {
      cueStart = data.metadata.start;
      cueEnd = data.metadata.end;
      attr["data-cue-start"] = cueStart;
      attr["data-cue-end"] = cueEnd;
    }
    if (data.start && data.end) {
      cueStart = data.start;
      cueEnd = data.end;
      attr["data-cue-start"] = cueStart;
      attr["data-cue-end"] = cueEnd;
    }
    if (data.dataDisplayStart) {
      attr["data-display-start"] = data.dataDisplayStart;
    }
    if (isDefined(data.strictMode)) {
      attr["data-strict"] = data.strictMode;
    }

    const moTag = document.createElement("div");
    for (const key in attr) {
      if (attr[key] !== undefined) {
        moTag.setAttribute(key, attr[key]);
      }
    }
    return moTag;
  },

  /**
   * Create google attachment html object
   */
  createInlineGoogleAttachmentTag(data) {
    const attr = {
      class: "google-attachment",
      name: "google-attachment",
      "data-id": data.id,
      "data-type": data.dataType,
      "data-context": data.dataContext,
      "data-usage": data.usage,
    };
    if (data.height && data.width) {
      attr["data-height"] = data.height;
      attr["data-width"] = data.width;
    }
    const tag = document.createElement("div");
    for (const key in attr) {
      if (attr[key] !== undefined) {
        tag.setAttribute(key, attr[key]);
      }
    }
    return tag;
  },

  /**
   * Create external media structure html object
   */
  createInlineExternalMediaTag(data) {
    const attr = {
      class: "sls-external-media",
      name: "sls-external-media",
      "data-uuid": data.uuid,
      "data-type": "external",
      "media-type": data.type.toUpperCase(),
      "data-title": data.title,
      "data-url": data.url,
    };

    const moTag = document.createElement("div");
    for (const key in attr) {
      if (attr[key] !== undefined) {
        moTag.setAttribute(key, attr[key]);
      }
    }
    return moTag;
  },

  /**
   * Create ugc structure html object
   */
  createInlineUgcMediaTag(data) {
    const attr = {
      class: "sls-media",
      name: "sls-media",
      "data-uuid": data.uuid,
      "data-type": "ugc",
      "media-type": data.type.toUpperCase(),
    };

    const moTag = document.createElement("div");
    for (const key in attr) {
      if (attr[key] !== undefined) {
        moTag.setAttribute(key, attr[key]);
      }
    }
    return moTag;
  },

  insertInlineAttachment(editor, data) {
    const placeholder = editor.dom.createHTML("div", {
      class: "inline-placeholder",
      "data-uuid": data.uuid,
      "data-type": data.type,
      "media-type": data.mediaType,
      "data-file-name": data.fileName,
      "data-resource-domain": data.resourceDomain,
      "is-add-new-media": data.isAddNewMedia,
    });
    editor.undoManager.ignore(() => {
      editor.insertContent(placeholder);
    });
  },

  insertFibBlankElement(editor) {
    const placeholder = editor.dom.createHTML("input", {
      class: "fill-in-box",
      readonly: "readonly",
      type: "text",
      value: "",
      contenteditable: false,
      id: generateBlankId(),
    });
    editor.insertContent(placeholder);
  },

  /**
   * Function to insert inline google attachment placeholder
   */
  insertInlineGoogleAttachment(editor, data) {
    const placeholder = editor.dom.createHTML("div", {
      class: `google-attachment-inline-placeholder`,
      "data-id": data.id,
      "data-type": data.dataType,
      "data-context": data.dataContext,
      "data-processStatus": data.processStatus,
      "data-usage": data.usage,
    });
    editor.insertContent(placeholder);
  },

  /**
   * Function to insert inline text-to-speech attachment and call the function to convert it
   */
  createInlineTextToSpeechAttachment(editor, data, events = {}) {
    const placeholder = editor.dom.createHTML(
      "div",
      {
        class: "sls-media sls-tts",
        name: "sls-media sls-tts",
        "data-type": "mo",
        "data-uuid": data.audioUuid,
        "media-type": MEDIA_TYPE.AUDIO,
      },
      data.audioText,
    );

    editor.insertContent(placeholder);
    this.renderInlineTextToSpeech(editor, ".sls-tts", events);
    editor.save();
  },

  /**
   * Function to convert inline placeholder to google attachment components for editor
   */
  renderInlineGoogleAttachment(
    editor,
    openInNewTabOnCreate,
    selector,
    duplicatedByEditingTeamMember,
    events = {},
  ) {
    DOMUtils.elOf(editor.getBody(), selector).forEach((element) => {
      // retrieve Google Attachment information.

      const data = this.getAttachmentDataForTag(element);
      data.openInNewTabOnCreate = openInNewTabOnCreate;
      const richTextEditor = editor.options.get("richTextEditor");
      data.tinyMceType = richTextEditor.kind;
      data.duplicatedByEditingTeamMember = duplicatedByEditingTeamMember;

      // Create non-editable wrapper layer.
      this.encloseElementWithWrapper(element, {
        class: "google-attachment-wrapper",
        contenteditable: false,
      });

      // Create the google attachment tag, used for clean up to convert to tag format.
      // Removed 'google-attachment' class to enable this function to be called repeatedly
      // subsequent calls will not changed rendered content.
      const attachment = this.createInlineGoogleAttachmentTag(data);
      DOMUtils.removeClass(attachment, "google-attachment");
      DOMUtils.addClass(attachment, "google-attachment-tag");

      // Pass the richtext context to the Google Attachment
      data.context = richTextEditor.richTextContext;

      data.questionAttemptContext = richTextEditor.questionAttemptContext;

      data.mode = "edit";

      if (richTextEditor.config?.plugins?.google)
        data.config = richTextEditor.config.plugins.google.googleServiceConfig;

      // Create the corresponding Google Attachment component.

      const GoogleAttachmentApp = createApp(GoogleAttachment, {
        ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
        ...data,
        ...events,
      });
      AppInstanceUtils.installCommonPlugin(GoogleAttachmentApp);
      const googleAttachment = GoogleAttachmentApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(googleAttachment);
      googleAttachment.$el.parentNode.appendChild(attachment);
      richTextEditor.googleAttachments.push(googleAttachment);

      // add <br> if last item is Google Attachment attachment, so user can add cursor after
      const mediaWrapper = googleAttachment.$el.closest(".google-attachment-wrapper");
      this.addNewLineIfIsLastElement(mediaWrapper);
    });
  },

  /**
   * Function to convert inline placeholder to text-to-speech attachment components for editor
   */
  renderInlineTextToSpeech(editor, selector, events = {}) {
    const richTextEditor = editor.options.get("richTextEditor");
    DOMUtils.elOf(editor.getBody(), selector).forEach((element) => {
      const propsData = {
        context: richTextEditor.richTextContext,
        audioUuid: element.getAttribute("data-uuid"),
        audioText: element.innerHTML,
        isInline: true,
        fromSE: element.classList.contains("from-se"),
      };

      this.encloseElementWithWrapper(element, {
        class: "sls-tts-wrapper",
        "data-uuid": element.getAttribute("data-uuid"),
        contenteditable: false,
      });

      const TextToSpeechApp = createApp(TextToSpeech, {
        ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
        ...propsData,
        ...events,
      });
      AppInstanceUtils.installCommonPlugin(TextToSpeechApp);
      const richTextTextToSpeech = TextToSpeechApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(richTextTextToSpeech);
      const ttsWrapper = richTextTextToSpeech.$el.closest(".sls-tts-wrapper");
      this.addNewLineIfIsLastElement(ttsWrapper);
    });
  },

  /**
   * Function to parse media tags and convert them to media components for the editor.
   * @param editor - The richtext editor
   * @param selector - The media tag/placeholder selector.
   * @param events - Array of events to bind on the media components.
   */
  renderInlineMedia(editor, selector, events = {}, rerender = false) {
    // TODO ron: to double check the refactoring on this function from the MO and UGC plugins.
    DOMUtils.elOf(editor.getBody(), selector).forEach((element) => {
      //return if its tts MO
      if (DOMUtils.hasClass(element, "sls-tts")) {
        return;
      }
      const richTextEditor = editor.options.get("richTextEditor");
      const mediaList = editor.options.get("mediaList");
      // retrieve media information.
      const mediaData = this.getMediaDataFromMediaTag(element);

      // retain flag when rerender
      if (!mediaData.isAddNewMedia && !isEmpty(mediaList)) {
        const media = mediaList.find((item) => item.uuid === mediaData.uuid);
        mediaData.isAddNewMedia = media?.isAddNewMedia ?? false;
      }

      // Create non-editable wrapper layer.
      this.encloseElementWithWrapper(element, {
        class: "sls-media-wrapper",
        contenteditable: false,
      });

      // Create the media tag, used for clean up to convert to tag format.
      // Removed 'sls-media' class to enable this function to be called repeatedly
      // subsequent calls will not changed rendered content.
      const slsMedia = this.createInlineMediaTag(mediaData);
      DOMUtils.removeClass(slsMedia, "sls-media");
      DOMUtils.addClass(slsMedia, "sls-media-tag");
      if (mediaData.isFromAttemptImport) {
        DOMUtils.addClass(slsMedia, "attempt-import");
      }
      mediaData.context = richTextEditor.richTextContext;

      // Create the corresponding Media component.
      const mediaApp = createApp(MediaComponent, {
        ...mediaData,
        ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
        ...events,
        onImageReady: richTextEditor.handleImageReady,
        hasSEAudio: richTextEditor.hasSEAudio,
        parentAnchor: richTextEditor.anchor,
        isSEGraded: richTextEditor.isSEGraded,
        html5InteractiveConfig: richTextEditor.config?.html5InteractiveConfig,
        questionAttemptUuid: richTextEditor.questionAttemptUuid,
        editor: editor,
        showDrawingRestriction: richTextEditor.showDrawingRestriction,
      });

      AppInstanceUtils.installCommonPlugin(mediaApp);
      mediaApp.provide("annotationContext", richTextEditor.annotationContext);
      mediaApp.provide("questionContext", richTextEditor.questionContext);
      mediaApp.provide("resourceContext", richTextEditor.resourceContext);

      // Mount media component to media element
      const mediaComponent = mediaApp.mount(element);
      if (rerender) {
        events["onMediaLoaded"] = () => {
          nextTick(() => {
            editor.selection.select(mediaComponent.$el.closest(".sls-media-wrapper"));
            ScrollUtils.scrollToTopWithHeaderOffset(mediaComponent.$el, editor.getBody());
          });
        };
      }

      DOMUtils.replaceComponentWrapperWithComponentElement(mediaComponent);

      // include media tag
      mediaComponent.$el.parentNode.appendChild(slsMedia);

      mediaList.push(mediaComponent);

      // add <br> if last item is media attachment, so user can add cursor after
      // FIXME: revisit this as in we shouldn't put whitespace
      const mediaWrapper = mediaComponent.$el.closest(".sls-media-wrapper");
      this.addNewLineIfIsLastElement(mediaWrapper);
    });
  },

  /**
   * Function to retrieve a media (MO/UGC) data information from the media tag.
   * @param element - The media tag to retrieve data from.
   */
  getMediaDataFromMediaTag(element) {
    const data = {};
    data.uuid = this.getValueOrDefault(element.getAttribute("data-uuid"));
    data.fileName = this.getValueOrDefault(element.getAttribute("data-file-name"));
    data.width = this.getValueOrDefault(element.getAttribute("data-width"));
    data.height = this.getValueOrDefault(element.getAttribute("data-height"));
    data.cuePoints = {
      start: this.getValueOrDefault(element.getAttribute("data-cue-start")),
      end: this.getValueOrDefault(element.getAttribute("data-cue-end")),
    };
    data.dataDisplayStart = this.getValueOrDefault(element.getAttribute("data-display-start"));
    // handle media and data type
    const dataType = this.getValueOrDefault(element.getAttribute("data-type"));
    if (DOMUtils.hasClass(element, "sls-media-object")) {
      // legacy media object structure (sls-media-object)
      data.mediatype = dataType.toLowerCase().trim();
      data.type = "mo";
    } else {
      const mediaType = element.getAttribute("media-type");
      data.mediatype = mediaType.toLowerCase().trim();
      data.type = dataType;
    }
    data.isFromAttemptImport = DOMUtils.hasClass(element, "attempt-import");
    if (notNull(data.dataDisplayStart)) {
      data.mediatype = MEDIA_TYPE.IMAGE;
    }
    data.resourceDomain = this.getValueOrDefault(element.getAttribute("data-resource-domain"));
    data.isInline = true;
    data.lazyload = false;
    data.strictMode = this.getValueOrDefault(element.getAttribute("data-strict") === "true", false);
    data.isUgc = data.type === "ugc";
    data.isTranscriptEnabled = this.getValueOrDefault(
      element.getAttribute("data-transcript-enabled") === "true",
      false,
    );
    data.transcriptionFileVersion = element.getAttribute("transcription-file-version");
    data.renderTranscript = data.isTranscriptEnabled;
    data.isAddNewMedia = this.getValueOrDefault(
      element.getAttribute("is-add-new-media") === "true",
      false,
    );
    return data;
  },

  /**
   * Function to retrieve a google attachment data information from the media tag.
   * @param element - The google attachment tag to retrieve data from.
   */
  getAttachmentDataForTag(element) {
    const data = {};
    data.id = this.getValueOrDefault(element.getAttribute("data-id"));
    data.width = this.getValueOrDefault(element.getAttribute("data-width"));
    data.height = this.getValueOrDefault(element.getAttribute("data-height"));
    data.isInline = true;
    data.lazyload = false;
    data.dataType = this.getValueOrDefault(element.getAttribute("data-type"));
    data.dataContext = this.getValueOrDefault(element.getAttribute("data-context"));
    data.processStatus = this.getValueOrDefault(element.getAttribute("data-processStatus"));
    data.usage = this.getValueOrDefault(element.getAttribute("data-usage"));
    return data;
  },

  /**
   * Function to return the value if it is valid (i.e. defined and not null),
   * otherwise return a configurable default value.
   * @param value
   * @param defaultValue
   * @returns {*}
   */
  getValueOrDefault(value, defaultValue) {
    if (isDefined(value) && notNull(value)) {
      // return value
      return value;
    }

    // return default value
    return isDefined(defaultValue) && notNull(defaultValue) ? defaultValue : null;
  },

  /**
   * Function to wrap an element with a layer of wrapper with configurable attributes.
   * @param element
   * @param wrapperConfig
   */
  encloseElementWithWrapper(element, wrapperConfig) {
    const wrapper = DOMUtils.createElement("div", wrapperConfig);
    element.parentNode.insertBefore(wrapper, element);
    wrapper.appendChild(element);
  },

  /**
   * Function to add a new line (paragraph div) to the editor if the element is the last element in the editor's content.
   * This allows the user to continue adding content below.
   * @param element
   */
  addNewLineIfIsLastElement(element) {
    const parentParagraph = element.closest("div.paragraph");
    if (this.IsLastElement(parentParagraph)) {
      const paragraph = DOMUtils.createElement("div", { class: "paragraph" });
      paragraph.innerHTML = "<br>";
      parentParagraph.parentNode.insertBefore(paragraph, parentParagraph.nextSibling);
    }
  },
  /**
   * Function to add a non-breaking space to the end of the element's parent paragraph
   *  if the element is the last element.
   * This allows the user to continue adding content in the same paragraph.
   * @param element
   */
  addNonBreakingSpaceIfIsLastElement(element) {
    if (this.IsLastElement(element)) {
      const parentParagraph = element.closest("div.paragraph");
      const textNode = document.createTextNode("\u00A0");
      parentParagraph.appendChild(textNode);
    }
  },
  IsLastElement(element) {
    return element && !element.nextSibling;
  },

  renderInlineExternalMedia(editor, rerender) {
    const richTextEditor = editor.options.get("richTextEditor");
    DOMUtils.elOf(editor.getBody(), ".sls-external-media:not(.loaded), .external-resource").forEach(
      (element) => {
        const dataType = element.getAttribute("data-type");
        const propsData = {
          uuid: element.getAttribute("data-uuid"),
          width: element.getAttribute("data-width"),
          height: element.getAttribute("data-height"),
          isInline: true,
          lazyload: false,
          type: "external",
          mediatype: element.getAttribute("media-type"),
          fileName: element.getAttribute("data-file-name"),
          altText: element.getAttribute("data-title"),
          mediaSrc: element.getAttribute("data-url"),
          isExternal: dataType === "external",
        };

        // wrap a layer with non editable
        const wrapper = DOMUtils.createElement("div", {
          class: "sls-media-wrapper",
          contenteditable: false,
        });
        element.parentNode?.insertBefore(wrapper, element);
        wrapper.appendChild(element);

        // Mount media component to media element, include the media tag
        const mediaApp = createApp(MediaComponent, {
          ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
          ...propsData,
          onMediaLoaded: () => {
            if (!rerender) {
              return;
            }
            editor.selection.select(mediaComponent.$el.closest(".sls-media-wrapper"));
            ScrollUtils.scrollToTopWithHeaderOffset(mediaComponent.$el, editor.getBody());
          },
        });
        AppInstanceUtils.installCommonPlugin(mediaApp);
        const mediaComponent = mediaApp.mount(element);

        element.removeAttribute("data-v-app");
        DOMUtils.replaceComponentWrapperWithComponentElement(mediaComponent);
        DOMUtils.addClass(element, "loaded");
        mediaComponent.$el.parentNode.appendChild(element);

        // add <br> if last item is media attachment, so user can add cursor after
        const mediaWrapper = mediaComponent.$el.closest(".sls-media-wrapper");
        if (!mediaWrapper.nextSibling) {
          const lineElement = document.createElement("br");
          lineElement.setAttribute("data-mce-bogus", 1);
          mediaWrapper.parentNode.insertBefore(lineElement, mediaWrapper.nextSibling);
        }
      },
    );
  },

  cleanUpPluginStructure(content, config) {
    const container = document.createElement("div");
    const containerAfterCleanUp = document.createElement("div");
    container.innerHTML = content;
    containerAfterCleanUp.innerHTML = content;

    // remove placeholder
    DOMUtils.elOf(containerAfterCleanUp, ".sls-media-wrapper").forEach((element) => {
      if (DOMUtils.elOf(element, ".inline-placeholder").length !== 0) {
        element.remove();
      }
    });
    DOMUtils.elOf(containerAfterCleanUp, ".inline-placeholder").forEach((element) => {
      element.remove();
    });
    // replace loaded media to original format (sls-media-object/sls-media)
    DOMUtils.elOf(container, ".sls-media-wrapper figure.media").forEach((element) => {
      const data = {
        uuid: element.getAttribute("data-uuid"),
        type: element.getAttribute("data-type"),
        title: element.getAttribute("data-title"),
        url: element.getAttribute("data-url"),
      };

      if (data.type === "VIDEO" || data.type === "AUDIO") {
        data.start = element.getAttribute("data-start");
        data.end = element.getAttribute("data-end");
      }

      const selector = `figure.media[data-uuid='${data.uuid}']`;
      const media = DOMUtils.elOf(containerAfterCleanUp, selector)[0];
      const mediaWrapper = media.closest(".sls-media-wrapper");

      if (config.type === "external") {
        if (data.title && data.url) {
          mediaWrapper?.replaceWith(this.createInlineExternalMediaTag(data));
        }
      } else if (config.type === "ugc") {
        mediaWrapper?.replaceWith(this.createInlineUgcMediaTag(data));
      } else {
        mediaWrapper?.replaceWith(this.createInlineMediaObjectTag(data));
      }
    });

    DOMUtils.elOf(containerAfterCleanUp, "br[data-mce-bogus]").forEach((element) => {
      element.remove();
    });

    return containerAfterCleanUp.innerHTML;
  },

  revertImageBackToMathML(child) {
    if (DOMUtils.hasClass(child, "Wirisformula")) {
      const wirisFormula = child.getAttribute("data-mathml");
      if (!isEmpty(wirisFormula)) {
        const wiris = DOMUtils.createElement("div");
        wiris.innerHTML = wirisFormula;
        child.parentElement.replaceChild(wiris.firstChild, child);
      }
    } else {
      const childElements = DOMUtils.elOf(child, ".Wirisformula");

      childElements.forEach((childElement) => {
        const wirisFormula = childElement.getAttribute("data-mathml");
        if (!isEmpty(wirisFormula)) {
          const wiris = DOMUtils.createElement("div");
          wiris.innerHTML = wirisFormula;
          childElement.parentElement?.replaceChild(wiris.firstChild, childElement);
        }
      });
    }

    return child;
  },
  removeMediaAndStripHTML(source) {
    const fragment = document.createDocumentFragment();
    const element = document.createElement("div");
    //remove wiris content for char/word count and char limit
    source = source.replace(/<math[^]*?[^]<\/math>/g, "");
    // remove tags with only &nbsp; (e.g. empty table cells) to exclude them (as spaces in innerText) from textContent
    // except for <span ..>&nbsp;</span> (SLSUAT-4751)
    source = source.replace(/(>)(&nbsp;)+(<\/(?!span\b)[^>]+>)/g, "$1$3");
    fragment.appendChild(element);
    element.innerHTML = source;
    const children = fragment.firstChild?.children;
    for (const child of children) {
      const mediaFileNameElements = DOMUtils.elOf(
        child,
        ".media, .sls-media, .sls-tts, .sls-speech-evaluation",
      );
      mediaFileNameElements.forEach((mediaElement) => {
        mediaElement.remove();
      });
    }
    // get text content
    const textContent = fragment.firstChild?.innerText;
    return textContent;
  },
  stripHTML(source) {
    const fragment = document.createDocumentFragment();
    const element = document.createElement("div");
    //remove table and wiris content
    source = source.replace(/<table[^]*?[^]<\/table>/g, "");
    source = source.replace(/<math[^]*?[^]<\/math>/g, "");
    fragment.appendChild(element);
    element.innerHTML = source;
    const children = fragment.firstChild?.children;
    for (const child of children) {
      const mediaFileNameElements = DOMUtils.elOf(
        child,
        ".media, .sls-media, .sls-youtube, .sls-embed-website, .richtext-tooltip, .sls-tts, .sls-speech-evaluation",
      );
      mediaFileNameElements.forEach((mediaElement) => {
        mediaElement.remove();
      });
    }
    //remove extra white spaces
    fragment.firstChild.innerText = fragment.firstChild?.innerText.replace(/\s+/g, " ").trim();
    return fragment.firstChild?.innerHTML;
  },
  stripUnsupportedDivAttributes(content, supportedDivAttribute) {
    const elem = DOMUtils.createElement("div");
    elem.innerHTML = content;

    if (!isEmpty(content) && !isEmpty(supportedDivAttribute)) {
      // lists of available div attributes which is aligned with backend whiteList
      const allowedAttributes = supportedDivAttribute.split(",");

      // add back "class" attribute as to be retained in original content
      allowedAttributes.push("class");

      DOMUtils.elOf(elem, `*`).forEach((element) => {
        const tagName = isDefined(element.tagName) ? element.tagName.toLowerCase() : "";

        // stripping not allowed attributes in div
        if (tagName === "div") {
          for (let i = element.attributes.length - 1; i >= 0; i--) {
            const attr = element.attributes[i];
            if (attr && !allowedAttributes.includes(attr.name)) {
              element.removeAttribute(attr.name);
              console.log("tag attribute is removed, tag:" + tagName + ", attr:" + attr.name);
            }
          }
        }
      });
    }
    return elem;
  },
  stripUnsupportedContent(element) {
    let content = element.innerHTML;
    if (!isEmpty(content)) {
      // strip HTML comment (anything that matches between <!-- and -->)
      const commentRegex = /<!--[\s\S]*?-->/g;
      if (commentRegex.test(content)) {
        content = content.replace(commentRegex, "");
      }

      // replace namespace prefix, i.e <o:p> to <p>
      const namespacedTagRegex = /<(\w+):(\w+)([^>]*)>([\s\S]*?)<\/\1:\2>/g;
      if (namespacedTagRegex.test(content)) {
        content = content.replace(namespacedTagRegex, `<$2$3>$4</$2>`);
      }
    }
    element.innerHTML = content;
  },
  getFileExtension(fileNameWithExt) {
    return fileNameWithExt.split(".").pop();
  },

  isJson(value) {
    return /^[\],:{}\s]*$/.test(
      value
        .replace(/\\["\\/bfnrtu]/g, "@")
        .replace(/"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g, "]")
        .replace(/(?:^|:|,)(?:\s*\[)+/g, ""),
    );
  },

  isNonCharacterKeystroke(code) {
    return (
      code === "Enter" ||
      code === "AltLeft" ||
      code === "AltRight" ||
      code === "Alt" ||
      code === "ArrowLeft" ||
      code === "ArrowRight" ||
      code === "ArrowUp" ||
      code === "ArrowDown" ||
      code === "ControlLeft" ||
      code === "ControlRight" ||
      code === "Control" ||
      code === "ShiftLeft" ||
      code === "ShiftRight" ||
      code === "CapsLock" ||
      code === "Delete" ||
      code === "Backspace"
    );
  },

  isEnterKeystroke(code) {
    return code === "Enter" || code === "NumpadEnter";
  },

  isEmpty(text) {
    const container = DOMUtils.createElement("div");
    container.innerHTML = text;

    if (!text) return true;

    //PLUGINS
    const WIRIS = {
      containPluginElements: this.containWirisPluginElements,
    };
    const MEDIA_OBJECT = {
      containPluginElements: function (container) {
        const selector = ".sls-media, .sls-media-object, figure.media";
        return DOMUtils.elOf(container, selector).length > 0;
      },
    };
    const HTML5_INTERACTIVE = {
      containPluginElements: function (container) {
        const selector = ".sls-media, .sls-media-object, figure.media";
        return DOMUtils.elOf(container, selector).length > 0;
      },
    };
    const GOOGLE = {
      containPluginElements: function (container) {
        const selector = ".google-attachment";
        return DOMUtils.elOf(container, selector).length > 0;
      },
    };
    const EMBED_WEBSITE = {
      containPluginElements: function (container) {
        const selector = ".embed-website-wrapper, .sls-embed-website";
        return DOMUtils.elOf(container, selector).length > 0;
      },
    };
    const YOUTUBE = {
      containPluginElements: function (container) {
        return DOMUtils.elOf(container, ".sls-youtube").length > 0;
      },
    };
    const STP_TOOLTIP = {
      containPluginElements: function (container) {
        return DOMUtils.elOf(container, ".teacher-stp").length > 0;
      },
    };

    const TEXT_TO_SPEECH = {
      containPluginElements: function (container) {
        return DOMUtils.elOf(container, ".sls-tts").length > 0;
      },
    };

    const SPEECH_EVALUATION = {
      containPluginElements: function (container) {
        return DOMUtils.elOf(container, ".sls-speech-evaluation").length > 0;
      },
    };

    const DYNAMIC_LINK = {
      containPluginElements: function (container) {
        return DynamicLinkUtils.getAllDynamicLinksFromContainer(container)?.length > 0;
      },
    };

    const PLUGINS = [
      WIRIS,
      MEDIA_OBJECT,
      GOOGLE,
      EMBED_WEBSITE,
      YOUTUBE,
      STP_TOOLTIP,
      TEXT_TO_SPEECH,
      SPEECH_EVALUATION,
      DYNAMIC_LINK,
      HTML5_INTERACTIVE,
    ];

    if (this.containPluginElements(container, PLUGINS)) return false;
    if (this.containCommonElements(text)) return false;

    const content = container.textContent.replace(/\n$/, ""); // removes trailing newline (tinyMCE behavior)
    return content.trim() === "";
  },

  containPluginElements(container, plugins) {
    let containElements = false;
    for (const plugin of Object.keys(plugins)) {
      if (plugins[plugin] && plugins[plugin].containPluginElements) {
        if (plugins[plugin].containPluginElements(container)) {
          containElements = true;
          break;
        }
      }
    }
    return containElements;
  },

  containCommonElements(htmlContent) {
    if (isEmpty(htmlContent)) {
      return false;
    }
    const containsImgTag = htmlContent.indexOf("<img");
    const containsInputTag = htmlContent.indexOf("<input");
    const containsIframeTag = htmlContent.indexOf("<iframe");
    if (containsImgTag > -1 || containsInputTag > -1 || containsIframeTag > -1) {
      return true;
    }
    return false;
  },
  containMediaPluginElements(container) {
    const selector = ".sls-media, .sls-media-object, .sls-external-media";
    return DOMUtils.elOf(container, selector).length > 0;
  },
  containWirisPluginElements(container) {
    return DOMUtils.elOf(container, "math").length > 0;
  },
  getGoogleDocumentIdsFromRichText(richTextContent) {
    const googleDocumentList = [];
    if (typeof richTextContent === "string") {
      richTextContent = this.wrapTextContentWithDiv(richTextContent);
      const elements = DOMUtils.elOf(richTextContent, ".google-attachment");
      elements.forEach((element) => {
        googleDocumentList.push(this.getValueOrDefault(element.getAttribute("data-id")));
      });
    }
    return googleDocumentList;
  },
  replaceAttribute(htmlContent, selector, attributeMap) {
    const container = document.createElement("div");
    container.innerHTML = htmlContent;

    // remove placeholder
    DOMUtils.elOf(container, selector).forEach((element) => {
      for (const [attributeKey, attributeValue] of Object.entries(attributeMap)) {
        element.setAttribute(attributeKey, attributeValue);
      }
    });

    return container.innerHTML;
  },

  removeNodeFromEditor(editor, targetNode) {
    const parentNode = targetNode.parentNode;
    if (!parentNode) return;

    const undoManager = editor.undoManager;
    if (undoManager.data && undoManager.data.length === 1) {
      // SLSSIT-5656
      // 1. case when undo stack size is 1, this is the first mutation action after first load.
      // 2. Due to the async loading of custom components, the editor's initial undo stack
      //    is taking the snapshot of the pre-loaded placeholder tags, which is not correct.
      // 3. Hence, we will do a reset (clear all and take the current state as an undo snapshot).
      // 4. So that when this action is undone, the editor will be load back the loaded custom components.
      undoManager.reset();
    }

    // get the previous sibling node, if exist, for caret targeting.
    const prevSiblingNode = targetNode.previousSibling;
    const nextSiblingNode = targetNode.nextSibling;

    // remove the target node
    parentNode.removeChild(targetNode);

    // logic to determine the caret position after deleting the target node
    if (prevSiblingNode) {
      // if there are content before the delete node:
      // then put caret after this content
      editor.selection.select(prevSiblingNode);
      editor.selection.collapse();
    } else if (nextSiblingNode) {
      // if there are no content before the delete node, but got content after:
      // then put the caret before the content after the delete node.
      editor.selection.select(nextSiblingNode);
      editor.selection.collapse(true);
    } else {
      // if delete node has no siblings:
      // then create an <br> element and put the caret after the <br>
      parentNode.innerHTML = "<br>";
      editor.selection.select(parentNode.firstElementChild);
      editor.selection.collapse();
    }

    // trigger change event (save) upon removing
    editor.save();
  },
  wrapTextContentWithDiv(text, divConfig) {
    const div = DOMUtils.createElement("div", divConfig);
    div.innerHTML = text;
    return div;
  },
  wrapTextContentWithDivUsingInnerText(text, divConfig) {
    const div = DOMUtils.createElement("div", divConfig);
    div.innerText = text;
    return div;
  },
  // To update the element for Google Document in attempt side after duplication from lesson
  replaceGoogleAttachmentInEditorForAttempt(editor, data, existingGoogleComponent, className) {
    editor.selection.select(existingGoogleComponent);
    const div = DOMUtils.createElement("div", {
      class: className,
      "data-id": data.id,
      "data-type": data.dataType,
      "data-context": data.dataContext,
      "data-processStatus": data.processStatus,
    });
    editor.insertContent(div.outerHTML);
  },
  replaceElement(existingElement, newDivConfig) {
    const newElement = DOMUtils.createElement("div", newDivConfig);
    existingElement.replaceWith(newElement);
    return newElement;
  },
  splitTextByNewLine(fullText) {
    return fullText.split(/\r?\n/);
  },
  splitTextByDelimiters(fullText, delimiters) {
    const splitText = fullText.match(delimiters);
    if (!isEmpty(splitText)) {
      return splitText;
    } else {
      return [fullText];
    }
  },
  getDelimiters(encoding) {
    switch (encoding) {
      case "EN":
        return /([^.?!\n]+[.?!\n]+)|([^.!?\n]+$)/g;
      case "ZH":
        return /([^。？！…\n]+[。？！…\n]+)|([^。？！…\n]+$)/g;
      case "MA":
        return /([^.？！…\n]+[.？！…\n]+)|([^.？！…\n]+$)/g;
      case "TA":
        return /([^.?!…\n]+[.?!…\n]+)|([^.?!…\n]+$)/g;
    }
  },
  validateRichTextForAIResponse(richTextContent, whitelist) {
    const ele = document.createElement("div");
    ele.innerHTML = richTextContent;
    const cachedAttributes = new Map();
    const result = this.validateHtmlTags(ele, whitelist, cachedAttributes);

    // If image tag is found, return false and reject
    if (!!ele.querySelector("img")) {
      return false;
    }

    return result;
  },
  validateHtmlTags(htmlContent, whitelist, cachedAttributes) {
    if (!htmlContent.tagName) {
      return false;
    }

    const tagName = htmlContent.tagName.toLowerCase();
    const whitelistData = whitelist.data;

    if (!whitelistData[tagName]) {
      return false;
    }

    let commonAllowedAttributes = cachedAttributes.get("common");
    if (!commonAllowedAttributes) {
      commonAllowedAttributes =
        whitelistData[":all"] && whitelistData[":all"].attributes
          ? whitelistData[":all"].attributes.split(",")
          : [];
      cachedAttributes.set("common", commonAllowedAttributes);
    }

    let taggedAllowedAttributes = cachedAttributes.get(tagName);
    if (!taggedAllowedAttributes) {
      taggedAllowedAttributes =
        whitelistData[tagName] && whitelistData[tagName].attributes
          ? whitelistData[tagName].attributes.split(",")
          : [];
      cachedAttributes.set(tagName, taggedAllowedAttributes);
    }

    const taggedEnforcedAttributes =
      whitelistData[tagName] && whitelistData[tagName].enforcedAttributes
        ? whitelistData[tagName].enforcedAttributes
        : null;

    // Missing enforced tags
    if (
      taggedEnforcedAttributes &&
      Object.keys(taggedEnforcedAttributes).length > 0 &&
      htmlContent.attributes.length === 0
    ) {
      return false;
    }

    if (htmlContent.attributes.length > 0) {
      const allowedAttributes = [...taggedAllowedAttributes, ...commonAllowedAttributes];
      for (const attribute of htmlContent.attributes) {
        // Allowed Attribute Checks
        if (allowedAttributes.indexOf(attribute.name) < 0) {
          return false;
        }

        // Enforced Attribute Checks
        const enforcedAttributeObject = taggedEnforcedAttributes
          ? taggedEnforcedAttributes[attribute.name]
          : null;
        if (!enforcedAttributeObject) continue;

        const matcherType = enforcedAttributeObject.matcher;
        const matchValues = enforcedAttributeObject.values.split(",");

        switch (matcherType) {
          case "exact": {
            if (!matchValues.includes(attribute.value)) return false;
            break;
          }
          case "starts_with": {
            if (!matchValues.some((value) => attribute.value.startsWith(value))) return false;
            break;
          }
        }
      }
    }

    if (htmlContent.children.length > 0) {
      let result = true;
      htmlContent.children.forEach((i) => {
        result = result && this.validateHtmlTags(i, whitelist, cachedAttributes);
        if (!result) return result; // Stop checking there are already failures
      });
      return result;
    } else {
      return true;
    }
  },
  generateTitle(title, activityTeamName, studentName) {
    if (!isEmpty(activityTeamName) && !isEmpty(studentName)) {
      title = `[${activityTeamName} | ${studentName}] ${title}`;
    } else if (!isEmpty(studentName)) {
      title = `[${studentName}] ${title}`;
    }
    return title;
  },
  getMediaObjectFromContent(richTextContent) {
    const container = document.createElement("div");
    container.innerHTML = richTextContent;
    const elements = DOMUtils.elOf(container, ".sls-media");
    return elements.map((el) => this.getMediaDataFromMediaTag(el));
  },
  removeAlgebraKitScriptTags(content) {
    const element = document.createElement("div");
    element.innerHTML = content;

    const itemsToRemove = element.querySelectorAll("script[type='algebrakit/init-data']");
    for (let i = itemsToRemove.length - 1; i >= 0; i--) {
      const itemToRemove = itemsToRemove[i];
      itemToRemove.parentNode.removeChild(itemToRemove);
    }

    return element.innerHTML;
  },
  removeAlgebraKitContent(content) {
    const element = document.createElement("div");
    element.innerHTML = content;

    const targetEls = element.querySelectorAll(".sls-algebra-kit");
    targetEls.forEach((targetEl) => {
      [...targetEl.attributes].forEach((attribute) => targetEl.removeAttribute(attribute.name));
    });

    return element.innerHTML;
  },
  getGoogleDocumentFromContent(richTextContent) {
    const container = document.createElement("div");
    container.innerHTML = richTextContent;
    const elements = DOMUtils.elOf(container, ".google-attachment");
    return elements.map((el) => this.getAttachmentDataForTag(el));
  },

  /**
   * Replace UGC media blocks AFTER the first with "(media)".
   * The first media block is removed.
   *
   * Note:
   * - this is string-based transform, does NOT load media.
   * - it is intentionally conservative: only targets UGC media blocks.
   */
  replaceUgcMediaAfterFirst(html?: string): string {
    if (!html) return "";

    const ugcMediaBlockRegex = /<div class=\"sls-media\"[^>]*data-type=\"ugc\"[^>]*>\s*<\/div>/gi;

    let seen = 0;
    return html.replace(ugcMediaBlockRegex, (match) => {
      seen += 1;
      // keep the first media block
      if (seen === 1) {
        return match;
      }
      // replace 2nd and subsequent media blocks
      return `<b>(Media)</b>`;
    });
  },
  processToPreserveLineBreaks(el: HTMLElement) {
    const selector = "p, .paragraph";
    const formatTags = new Set(["B", "STRONG", "I", "EM"]);

    DOMUtils.elOf(el, selector).forEach((node: Element) => {
      const element = node as HTMLElement;
      const isParagraphEmpty = notNull(element.textContent) && isEmpty(element.textContent.trim());
      const nestedChildren = Array.from(element.querySelectorAll("*"));
      const hasOnlyFormatTags =
        nestedChildren.length > 0 && nestedChildren.every((child) => formatTags.has(child.tagName));

      // add line breaks for:
      // 1. completely empty paragraph with no HTML tags
      // 2. empty paragraph with ONLY empty formatting tags
      if (
        (isParagraphEmpty && notNull(element.innerHTML) && isEmpty(element.innerHTML.trim())) ||
        (isParagraphEmpty && hasOnlyFormatTags)
      ) {
        element.innerHTML = "<br>";
      }
    });
  },
};
